import type { IUserRepository, UserInvitation, UserRoleHistory, User } from '../interfaces/IUserRepository';
import { mockUsers, simulateDelay, maybeSimulateError } from './mockData';

export class MockUserRepository implements IUserRepository {
  private users: User[] = [...mockUsers];
  private invitations: UserInvitation[] = [];
  private roleHistory: UserRoleHistory[] = [];

  async getAll(tenantId: string): Promise<User[]> {
    await simulateDelay();
    maybeSimulateError();
    return this.users.filter(u => u.client_id === tenantId);
  }

  async getAllByPlatformTenant(_platformTenantId: string): Promise<User[]> {
    await simulateDelay();
    maybeSimulateError();
    return this.users;
  }

  async getById(id: string): Promise<User | null> {
    await simulateDelay();
    maybeSimulateError();
    return this.users.find(u => u.id === id) || null;
  }

  async getByEmail(email: string): Promise<User | null> {
    await simulateDelay();
    maybeSimulateError();
    return this.users.find(u => u.email === email) || null;
  }

  async searchUsers(tenantId: string, query: string): Promise<User[]> {
    await simulateDelay();
    maybeSimulateError();
    const lowerQuery = query.toLowerCase();
    return this.users.filter(u => 
      u.client_id === tenantId && 
      (u.email.toLowerCase().includes(lowerQuery) ||
       u.first_name?.toLowerCase().includes(lowerQuery) ||
       u.last_name?.toLowerCase().includes(lowerQuery))
    );
  }

  async inviteUser(data: {
    email: string;
    role: string;
    client_id: string;
    invited_by: string;
  }): Promise<UserInvitation> {
    await simulateDelay();
    maybeSimulateError();
    
    const invitation: UserInvitation = {
      id: crypto.randomUUID(),
      email: data.email,
      role: data.role,
      invited_by: data.invited_by,
      invited_at: new Date().toISOString(),
      accepted: false,
      client_id: data.client_id,
    };
    
    this.invitations.push(invitation);
    return invitation;
  }

  async updateUser(id: string, data: Partial<User>): Promise<User> {
    await simulateDelay();
    maybeSimulateError();
    
    const index = this.users.findIndex(u => u.id === id);
    if (index === -1) throw new Error('User not found');
    
    this.users[index] = { ...this.users[index], ...data };
    return this.users[index];
  }

  async deactivateUser(id: string, deactivated_by: string): Promise<void> {
    await simulateDelay();
    maybeSimulateError();
    
    const user = await this.updateUser(id, { status: 'inactive' });
    
    // Log to history
    this.roleHistory.push({
      id: crypto.randomUUID(),
      user_id: id,
      role: user.role,
      changed_by: deactivated_by,
      changed_at: new Date().toISOString(),
      reason: 'User deactivated',
    });
  }

  async reactivateUser(id: string, reactivated_by: string): Promise<void> {
    await simulateDelay();
    maybeSimulateError();
    
    const user = await this.updateUser(id, { status: 'active' });
    
    // Log to history
    this.roleHistory.push({
      id: crypto.randomUUID(),
      user_id: id,
      role: user.role,
      changed_by: reactivated_by,
      changed_at: new Date().toISOString(),
      reason: 'User reactivated',
    });
  }

  async assignRole(userId: string, role: string, assigned_by: string): Promise<void> {
    await simulateDelay();
    maybeSimulateError();
    
    await this.updateUser(userId, { role });
    
    this.roleHistory.push({
      id: crypto.randomUUID(),
      user_id: userId,
      role,
      changed_by: assigned_by,
      changed_at: new Date().toISOString(),
      reason: 'Role assigned',
    });
  }

  async removeRole(userId: string, role: string, removed_by: string): Promise<void> {
    await simulateDelay();
    maybeSimulateError();
    
    this.roleHistory.push({
      id: crypto.randomUUID(),
      user_id: userId,
      role,
      changed_by: removed_by,
      changed_at: new Date().toISOString(),
      reason: 'Role removed',
    });
  }

  async getRoleHistory(userId: string): Promise<UserRoleHistory[]> {
    await simulateDelay();
    maybeSimulateError();
    return this.roleHistory.filter(h => h.user_id === userId);
  }

  async getPendingInvitations(tenantId: string): Promise<UserInvitation[]> {
    await simulateDelay();
    maybeSimulateError();
    return this.invitations.filter((i: any) => i.client_id === tenantId && !i.accepted);
  }

  async resendInvitation(invitationId: string): Promise<void> {
    await simulateDelay();
    maybeSimulateError();
    // Simulate resending
    // Invitation resent
  }

  async cancelInvitation(invitationId: string): Promise<void> {
    await simulateDelay();
    maybeSimulateError();
    const index = this.invitations.findIndex(i => i.id === invitationId);
    if (index !== -1) {
      this.invitations.splice(index, 1);
    }
  }
}
